import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  deleteSlaRule,
  getApiErrorMessage,
  listDepartments,
  listSlaRules,
} from '../../api/client';
import type { DepartmentSummary, SlaRule } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditSla, canWriteSla } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function SlaRulesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState<SlaRule[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const departmentFilter = searchParams.get('departmentId') ?? '';
  const canCreate = user && canWriteSla(user);
  const canModify = user && canEditSla(user);

  useEffect(() => {
    listDepartments().then(setDepartments).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    listSlaRules(departmentFilter || undefined)
      .then(setRules)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [departmentFilter]);

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this SLA rule?')) return;
    setDeletingId(id);
    setError('');
    try {
      await deleteSlaRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Response and resolution targets per department, category, and priority.
          </p>
        </div>
        {canCreate && (
          <Link to={PLATFORM_CONFIG.slaNew} className="btn btn-primary">
            Add rule
          </Link>
        )}
      </div>

      {!canModify && (
        <div className="alert alert-info">
          You have read-only access to SLA rules.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters card card-body" style={{ marginBottom: '1rem' }}>
        <div className="form-group">
          <label htmlFor="sla-dept-filter">Department</label>
          <select
            id="sla-dept-filter"
            className="form-control"
            value={departmentFilter}
            onChange={(e) => {
              const next = e.target.value;
              if (next) setSearchParams({ departmentId: next });
              else setSearchParams({});
            }}
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading SLA rules…</div>
        ) : rules.length === 0 ? (
          <div className="empty-state">No SLA rules configured.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>First response</th>
                  <th>Resolution</th>
                  <th>Status</th>
                  {canModify && <th />}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td>{r.department.name}</td>
                    <td>
                      <Badge label={r.category.name} />
                    </td>
                    <td>
                      <Badge label={r.priority} />
                    </td>
                    <td>{formatMinutes(r.firstResponseMinutes)}</td>
                    <td>{formatMinutes(r.resolutionMinutes)}</td>
                    <td>
                      <Badge
                        label={r.isActive ? 'ACTIVE' : 'INACTIVE'}
                        className={r.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                    {canModify && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Link
                          to={PLATFORM_CONFIG.slaRule(r.id)}
                          className="btn btn-secondary btn-sm"
                          style={{ marginRight: '0.5rem' }}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          disabled={deletingId === r.id}
                          onClick={() => handleDelete(r.id)}
                        >
                          {deletingId === r.id ? '…' : 'Delete'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
